// Global Instructions Rule Applied!

import React, { useMemo } from 'react'
import TableView from '../../../core/components/TableView/TableView.tsx'
import { createTableColumns } from '../../../ui_configs/table_grid_columns.ts'
import { useTabManagerContext } from '../../../core/app-layout/lib/TabManagerContext.tsx'
import { TABLE_FUNDS } from '../../../state_manager/globals.ts'
import { recordHasTotalLabel, stripTotalTextFromCellNode } from '../../../core/utils/recordRowBold.ts'
import type { ViewConfigColumn } from '../../../core/components/ViewConfig/types.ts'
import type { FilterValue, SortConfig } from '../../../state_manager/interfaces/funds.ts'
import type { FilterOptions, FilterConfig } from '../../../core/components/TableView/TableViewTypes.ts'

interface FundsTableProps {
  data: Record<string, unknown>[]
  loading: boolean
  loadingMore?: boolean
  hasMore?: boolean
  currentPage?: number
  gridColumns: ViewConfigColumn[]
  activeFilters?: FilterValue[]
  activeSorting?: SortConfig[]
  filterOptions?: FilterOptions
  filterOptionsLoading?: boolean
  onLoadMore?: (page: number, pageSize: number) => void | Promise<void>
  onFiltersChange?: (filters: FilterConfig) => void
  onSortChange?: (sortConfig: any) => void
}

export const FundsTable = React.memo(function FundsTable({
  data,
  loading,
  loadingMore = false,
  hasMore = false,
  currentPage = 1,
  gridColumns,
  activeFilters = [],
  activeSorting = [],
  filterOptions = {},
  filterOptionsLoading = false,
  onLoadMore,
  onFiltersChange,
  onSortChange
}: FundsTableProps) {
  const { openFormInTab } = useTabManagerContext()

  const openRecordFullColumns = useMemo(
    () => new Set(gridColumns.filter((c) => c.fieldKey === 'open-record-full').map((c) => c.valueKey)),
    [gridColumns]
  )

  const { columns: baseColumns, frozenColumns } = useMemo(() => {
    return createTableColumns({
      columns: gridColumns,
      fallbackRecordModuleKey: 'funds'
    })
  }, [gridColumns])

  const firstColumnValueKey = gridColumns[0]?.valueKey

  const columns = useMemo(() => {
    return baseColumns.map((col) => {
      const prevRender = col.render
      if (!prevRender) return col
      const isFirstColumn = firstColumnValueKey != null && col.key === firstColumnValueKey
      const opensRecordFull = openRecordFullColumns.has(String(col.key))
      return {
        ...col,
        render: (value: unknown, record: Record<string, unknown>, index: number, editingContext: unknown) => {
          const node = prevRender!(value, record, index, editingContext)
          const isTotalRow = recordHasTotalLabel(record)
          const renderedNode = isTotalRow && !isFirstColumn ? stripTotalTextFromCellNode(node) : node

          if (!opensRecordFull || isTotalRow) return renderedNode

          const fundId = typeof record.funds__id === 'string' ? record.funds__id : ''
          if (!fundId) return renderedNode

          return (
            <button
              type='button'
              className='cursor-pointer text-blue-600 hover:text-blue-700 hover:underline bg-transparent border-0 p-0 text-left'
              onClick={() => openFormInTab(TABLE_FUNDS, fundId, String(record.funds__name ?? ''))}
            >
              {renderedNode}
            </button>
          )
        }
      }
    })
  }, [baseColumns, firstColumnValueKey, openRecordFullColumns, openFormInTab])

  const dataSource = useMemo(() => {
    const src = data ?? []
    return src.map((row, i) => {
      const id = typeof row.funds__id === 'string' && row.funds__id ? row.funds__id : `total-${i}`
      return { ...row, id, key: id }
    })
  }, [data])

  // Convert activeFilters array → object for TableView's currentFilters prop.
  const currentFilters = useMemo(() => {
    if (!Array.isArray(activeFilters)) return activeFilters ?? {}
    return activeFilters.reduce((acc: Record<string, any>, f) => {
      acc[f.key] = f.value
      return acc
    }, {})
  }, [activeFilters])

  // TableView consumes a single-column sort config: { column, direction }.
  const currentSortConfig = useMemo(() => {
    if (!Array.isArray(activeSorting) || activeSorting.length === 0) return null
    const first = activeSorting[0]
    return { column: first.key, direction: first.direction }
  }, [activeSorting])

  if (columns.length === 0) return null

  return (
    <div className='flex-1 min-h-0 h-full w-full'>
      <TableView
        columns={columns}
        dataSource={dataSource}
        loading={loading}
        loadingMore={loadingMore}
        hasMore={hasMore}
        currentPage={currentPage}
        onLoadMore={onLoadMore}
        currentFilters={currentFilters}
        onFiltersChange={onFiltersChange}
        onSortChange={onSortChange}
        filterOptions={filterOptions}
        filterOptionsLoading={filterOptionsLoading}
        currentSortConfig={currentSortConfig}
        frozenColumns={frozenColumns}
        totalsMode='sticky-bottom'
        rowClassName={(record) => (recordHasTotalLabel(record) ? 'font-bold' : undefined)}
        headerStyle={{ padding: '5px', minHeight: 40, backgroundColor: '#f5f5f5' }}
      />
    </div>
  )
})

export default FundsTable
